// Client-side validation for the Standard Ordering flow.
// Rules visible in the Figma design:
//  - Quantity: cannot be 0, cannot exceed 200.
//  - Delivery date: cannot be in the past, cannot be more than 30 days from today.

import { fromISODate, toISODate } from '@/lib/utils'

export const MIN_QTY = 1
export const MAX_QTY = 200
export const MAX_DELIVERY_DAYS = 30

/** Normalise a date to local midnight so day-level comparisons are exact. */
export function startOfDay(d: Date): Date {
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  return copy
}

/** Earliest selectable delivery date: today (local midnight). */
export function minDeliveryDate(today = new Date()): Date {
  return startOfDay(today)
}

/** Latest selectable delivery date: today + 30 days. */
export function maxDeliveryDate(today = new Date()): Date {
  const d = startOfDay(today)
  d.setDate(d.getDate() + MAX_DELIVERY_DAYS)
  return d
}

/** Returns an error message, or null when the quantity is valid. */
export function validateQty(qty: number): string | null {
  if (!Number.isFinite(qty) || Number.isNaN(qty)) return 'Enter a quantity'
  if (qty < MIN_QTY) return 'Quantity cannot be 0'
  if (qty > MAX_QTY) return `Quantity cannot exceed ${MAX_QTY}`
  return null
}

/** Returns an error message, or null when the delivery date is valid. */
export function validateDeliveryDate(date: Date | null, today = new Date()): string | null {
  if (!date) return 'Select a delivery date'
  const day = startOfDay(date)
  if (day < minDeliveryDate(today)) return 'Delivery date cannot be a past date'
  if (day > maxDeliveryDate(today)) {
    return `Delivery date cannot be ${MAX_DELIVERY_DAYS} days from today`
  }
  return null
}

/** True when a date falls inside the allowed delivery window. */
export function isDeliveryDateSelectable(date: Date, today = new Date()): boolean {
  return validateDeliveryDate(date, today) === null
}

/**
 * A stored delivery date is "no longer available" for next-day fulfilment once it
 * is not strictly in the future (today or earlier) — the cart submit guard uses
 * this to flag stale lines and offer to reschedule them.
 */
export function isDeliveryDateStale(iso: string, today = new Date()): boolean {
  return fromISODate(iso) <= minDeliveryDate(today)
}

/** The next valid delivery date (tomorrow) as a Date. */
export function nextDeliveryDate(today = new Date()): Date {
  const d = minDeliveryDate(today)
  d.setDate(d.getDate() + 1)
  return d
}

/** The next valid delivery date (tomorrow) as an ISO string. */
export function nextValidDeliveryISO(today = new Date()): string {
  return toISODate(nextDeliveryDate(today))
}
